"use client";

import React, { useState, useEffect, useRef } from "react";
import { gsap } from "gsap";

interface PasswordGateProps {
  password: string;
  projectId: string;
  children: React.ReactNode;
}

const PasswordGate: React.FC<PasswordGateProps> = ({ password, projectId, children }) => {
  const [unlocked, setUnlocked] = useState(false);
  const [input, setInput] = useState("");
  const [error, setError] = useState(false);
  const [shake, setShake] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLDivElement>(null);
  const storageKey = `unlocked_${projectId}`;

  useEffect(() => {
    if (sessionStorage.getItem(storageKey) === "true") {
      setUnlocked(true);
      return;
    }

    const ctx = gsap.context(() => {
      gsap.fromTo(
        formRef.current,
        { opacity: 0, y: 32 },
        { opacity: 1, y: 0, duration: 0.7, ease: "power2.out" }
      );
    }, containerRef);

    return () => ctx.revert();
  }, [storageKey]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (input === password) {
      sessionStorage.setItem(storageKey, "true");

      gsap.to(containerRef.current, {
        opacity: 0,
        y: -20,
        duration: 0.4,
        ease: "power2.in",
        onComplete: () => setUnlocked(true),
      });
    } else {
      setError(true);
      setShake(true);
      setInput("");
      setTimeout(() => {
        setError(false);
        setShake(false);
      }, 600);
    }
  };

  if (unlocked) return <>{children}</>;

  return (
    <div
      ref={containerRef}
      className="min-h-screen bg-[#fafcfd] flex items-center justify-center px-6"
    >
      <div ref={formRef} className="w-full max-w-sm">
        {/* Lock icon */}
        <div className="mb-10 flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-full border border-[#e0e0e0] flex items-center justify-center">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#6f6f6f"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>
          <div className="text-center">
            <p className="text-xs uppercase tracking-[0.2em] text-[#6f6f6f] mb-2">
              protected project
            </p>
            <p className="text-[#111111] text-sm">
              Enter the password to view this work.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div
            className={`transition-transform duration-100 ${shake ? "animate-[shake_0.4s_ease]" : ""}`}
            style={
              shake
                ? { animation: "shake 0.4s ease" }
                : {}
            }
          >
            <input
              type="password"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Password"
              autoFocus
              className={`w-full border-b bg-transparent py-3 text-[#111111] text-sm placeholder:text-[#c0c0c0] outline-none transition-colors duration-200 ${
                error ? "border-red-400" : "border-[#e0e0e0] focus:border-[#111111]"
              }`}
            />
            {error && (
              <p className="mt-2 text-xs text-red-400">Incorrect password. Try again.</p>
            )}
          </div>

          <button
            type="submit"
            className="w-full py-3 bg-[#111111] text-white text-xs uppercase tracking-[0.15em] hover:bg-[#333] transition-colors duration-200"
          >
            Unlock
          </button>
        </form>
      </div>

      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-8px); }
          40% { transform: translateX(8px); }
          60% { transform: translateX(-6px); }
          80% { transform: translateX(4px); }
        }
      `}</style>
    </div>
  );
};

export default PasswordGate;
